import clsx from "clsx";
import { KeyboardEvent, useRef } from "react";
import { RadioButtonGroupOption } from "../RadioButtonGroup";

export interface VerticalRadioButtonGroupProps {
  name: string;
  value: string | number;
  onChange: (e: { target: { value: string | number } }) => void;
  options: RadioButtonGroupOption[];
  error?: string;
  disabled?: boolean;
}

export const VerticalRadioButtonGroup: React.FC<VerticalRadioButtonGroupProps> = ({
  name,
  value,
  onChange,
  options,
  error,
  disabled,
}) => {
  const radioGroupRef = useRef<HTMLDivElement>(null);

  // Handle keyboard navigation
  const handleKeyDown = (e: KeyboardEvent<HTMLLabelElement>, optionValue: string | number) => {
    const currentIndex = options.findIndex((opt) => String(opt.value) === String(optionValue));

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (currentIndex < options.length - 1) {
          const nextValue = options[currentIndex + 1].value;
          onChange({ target: { value: nextValue } });
          // Focus the next radio input
          const nextInput = radioGroupRef.current?.querySelector(
            `input[value="${String(nextValue)}"]`,
          ) as HTMLInputElement;
          nextInput?.focus();
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (currentIndex > 0) {
          const prevValue = options[currentIndex - 1].value;
          onChange({ target: { value: prevValue } });
          // Focus the previous radio input
          const prevInput = radioGroupRef.current?.querySelector(
            `input[value="${String(prevValue)}"]`,
          ) as HTMLInputElement;
          prevInput?.focus();
        }
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        onChange({ target: { value: optionValue } });
        break;
    }
  };

  return (
    <div className="space-y-2 tall:space-y-4" ref={radioGroupRef} role="radiogroup">
      {options.map((option) => {
        const isSelected = String(value) === String(option.value);
        return (
          <label
            key={option.value}
            className={clsx("block cursor-pointer rounded-lg border-2 p-2 tall:p-4 transition-all", {
              "border-blue-500 bg-blue-50": isSelected,
              "border-gray-200 hover:border-blue-300": !isSelected,
              "outline-none focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2": true,
            })}
            onKeyDown={(e) => handleKeyDown(e, option.value)}
          >
            <input
              type="radio"
              name={name}
              value={String(option.value)}
              checked={isSelected}
              onChange={() => onChange({ target: { value: option.value } })}
              className="sr-only"
              aria-checked={isSelected}
              tabIndex={isSelected ? 0 : -1}
              disabled={disabled}
            />
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100 fill-gray-800">
                {option.icon}
              </div>
              <div>
                <div className="font-medium">{option.label}</div>
                {option.description && <div className="text-sm text-gray-500">{option.description}</div>}
              </div>
            </div>
          </label>
        );
      })}
    </div>
  );
};
